import { MODULES } from './modules';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, patch, post, put, type Location } from './api';
import { Confirm, Modal, useToast, type ConfirmSpec } from './ui';
import { ActivityList, ActivityModal } from './ActivityModal';
import { ChangeHistory } from './ChangeHistory';

interface Off { on: string; by: string | null }
interface Staff { userId: number; name: string; phone: string; role: string; pinSet: boolean; lastSignIn: string | null; off: Off | null }
interface Person {
  employeeId: number; userId: number | null; name: string; phone: string; level: number; role: string;
  homeLocationId: number; extraLocationIds: number[]; pinSet: boolean; lastSignIn: string | null; off: Off | null;
}
interface Device { id: number; locationId: number; kind: 'office' | 'counter'; setUpAt: string; setUpBy: string | null; lastUsed: string | null; modules?: string[] }
interface AccessResp {
  summary: { admins: number; maxAdmins: number; managers: number; leads: number; hr: number };
  staff: Staff[]; people: Person[]; devices: Device[];
}

/** Managers & access (admins only), as in the prototype. */
/** Emailed PDFs come from the MyWunderland mailbox; each store's own address here gets the replies (owner 10/26).
 *  The mailbox password goes only in the NAS settings file (EMAIL_PASSWORD). */
function StoreEmails({ locations, showToast, onChanged }: { locations: Location[]; showToast: (m: string) => void; onChanged: () => void }) {
  const [draft, setDraft] = useState<Record<number, string>>({});
  const first = locations.find((l) => l.emailFrom);
  const from = first?.emailFrom ?? 'mywunderland@wunderlandgames.com', ready = !!first?.emailReady;
  const save = async (l: Location) => {
    const v = (draft[l.id] ?? l.email ?? '').trim();
    if (v === (l.email ?? '')) return;
    try {
      const r = await patch<{ email: string | null }>(`/api/admin/locations/${l.id}/email`, { email: v });
      showToast(r.email ? `Replies to ${l.name}’s emails go to ${r.email}.` : `${l.name}’s email address was removed.`); onChanged();
    } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Store email addresses</h2></div>
      <div style={{ fontSize: 13, color: '#4A5561' }}>“Email…” on the day board, week summary and rotation sends a PDF from <b>{from}</b>. Replies go to the store’s own address, typed here. The mailbox’s password (a Zoho app-specific password) goes only in the settings file on the NAS.{' '}
        <span className={ready ? 'smsnote' : 'smsnote bad'}>{ready ? 'Password is on the NAS' : 'Password not on the NAS yet: add EMAIL_PASSWORD'}</span></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {locations.map((l) => (
          <div key={l.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <b style={{ minWidth: 110 }}>{l.name}{l.demo ? ' (demo)' : ''}</b>
            <input className="inp" type="email" style={{ maxWidth: 300 }} placeholder={`${l.name.toLowerCase().replace(/[^a-z0-9]/g, '')}@wunderlandgames.com`} aria-label={`${l.name} email address for replies`}
              value={draft[l.id] ?? l.email ?? ''} onChange={(e) => setDraft((x) => ({ ...x, [l.id]: e.target.value }))} onBlur={() => void save(l)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
          </div>
        ))}
      </div>
    </>
  );
}

/** The Demo store (owner 10/22): made-up people and schedules for testing and training; it never texts anyone. */
type DemoInfo = { id: number; name: string; on: boolean; locations: number[] } | null;
const DEMO_TEXT = {
  safe: { name: 'Demo', title: 'Demo (practice)', what: 'For trying things out and training managers. Nothing is ever texted or emailed from it, not even to a real mobile number: everything that would have gone out shows in its Demo outbox.' },
  live: { name: 'Live Demo', title: 'Live Demo (real texts)', what: 'For live tests. Its made-up people are never texted, but a real mobile number put on one of its people gets real texts and can sign in on that phone, and messages and emails really go out.' },
} as const;

/** Managers & access › Demo stores (owner 10/22): admins only. Two kinds, each switched on or off and ticked per location. */
function DemoBox({ stores, setConfirm, showToast, onChanged }: { stores: Location[]; setConfirm: (c: ConfirmSpec) => void; showToast: (m: string) => void; onChanged: () => void }) {
  const [d, setD] = useState<{ safe: DemoInfo; live: DemoInfo } | null>(null);
  const load = useCallback(async () => { try { setD(await get<{ safe: DemoInfo; live: DemoInfo }>('/api/admin/demo')); } catch { /* shown as not loaded */ } }, []);
  useEffect(() => { void load(); }, [load]);
  const save = async (mode: 'safe' | 'live', on: boolean, locationIds: number[]) => {
    const old = d; setD((x) => (x && x[mode] ? { ...x, [mode]: { ...x[mode]!, on, locations: locationIds } } : x));
    try { await put('/api/admin/demo/settings', { mode, on, locationIds }); onChanged(); }
    catch (e) { setD(old); showToast(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };
  const go = (mode: 'safe' | 'live', what: 'build' | 'reset') => {
    const t = DEMO_TEXT[mode];
    setConfirm({
      title: what === 'build' ? `Set up the ${t.name} store?` : `Reset the ${t.name} store?`,
      text: what === 'build'
        ? `Adds a store called “${t.name}” with 25 made-up employees and three weeks of schedule (last week and this week posted, next week a draft), plus a few time-off requests and a message. It starts switched off: turn it on and tick the stores whose managers may open it. ${t.what}`
        : `Puts the ${t.name} store back to how it started: everything anyone changed there is set aside and a fresh one is made, with the same on/off and ticked stores. Real stores aren’t touched.`,
      ok: what === 'build' ? `Set up ${t.name}` : `Reset ${t.name}`,
      run: async () => {
        try { await post(`/api/admin/demo/${what}`, { mode }); showToast(what === 'build' ? `The ${t.name} store is ready. Turn it on below.` : `The ${t.name} store has been reset.`); await load(); onChanged(); }
        catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); }
      },
    });
  };
  const card = (mode: 'safe' | 'live') => {
    const t = DEMO_TEXT[mode], x = d?.[mode];
    const toggle = (id: number) => x && void save(mode, x.on, x.locations.includes(id) ? x.locations.filter((y) => y !== id) : [...x.locations, id]);
    return (
      <div key={mode} className="democard" style={{ border: `2px solid ${mode === 'live' ? '#B3261E' : '#D99A00'}`, borderRadius: 8, background: '#FFFFFF', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, flex: '1 1 340px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <b style={{ fontSize: 16 }}>{t.title}</b>
          {x && <span className={x.on ? 'accok' : 'accwait'}>{x.on ? 'On' : 'Off'}</span>}
          <span style={{ flex: '1 1 auto' }} />
          {x && <button className="btn2" onClick={() => void save(mode, !x.on, x.locations)}>{x.on ? 'Turn off' : 'Turn on'}</button>}
          {d && (x ? <button className="btn2" onClick={() => go(mode, 'reset')}>Reset…</button> : <button className="btn2" onClick={() => go(mode, 'build')}>Set up…</button>)}
        </div>
        <div style={{ fontSize: 13, color: '#4A5561' }}>{t.what}</div>
        {x ? <>
          <div style={{ fontSize: 13 }}><b>Managers (levels 7–9) who can open it, by store</b>{!x.on && <span style={{ color: '#4A5561' }}> · nobody but admins can open it while it’s off</span>}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
            {stores.map((l) => <label key={l.id} className="allday"><input type="checkbox" checked={x.locations.includes(l.id)} onChange={() => toggle(l.id)} aria-label={`${t.name}: ${l.name} managers can open it`} />{l.name}</label>)}
            {!stores.length && <span className="stnone">No stores yet.</span>}
          </div>
          <div style={{ fontSize: 12.5, color: '#4A5561' }}>When it’s on, HR can open it too. Shift leads and employees never can. Admins always can.</div>
        </> : <div style={{ fontSize: 13, color: '#4A5561' }}>Not set up yet.</div>}
      </div>
    );
  };
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 6 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Demo stores</h2>
      </div>
      <div style={{ fontSize: 13, color: '#4A5561' }}>Made-up stores with a full staff and schedule. Pick one in Location at the top to use it; what would have been texted shows under “Demo outbox”. To see the phone app, press “Sign-in code” on a made-up person and sign in with their number and that code.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>{card('safe')}{card('live')}</div>
    </>
  );
}

export function AccessPage({ locations, onLocationsChanged }: { locations: Location[]; onLocationsChanged: () => void }) {
  const [data, setData] = useState<AccessResp | null>(null);
  const [store, setStore] = useState<number | 'all'>('all');
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [adding, setAdding] = useState(false);
  const [deviceModal, setDeviceModal] = useState(false);
  const [addLoc, setAddLoc] = useState(false);
  const [code, setCode] = useState<{ title: string; code: string; text: string } | null>(null);
  const [err, setErr] = useState('');
  const [actFor, setActFor] = useState<{ employeeId?: number; userId?: number } | null>(null);
  const [toast, showToast] = useToast();
  const locName = (id: number) => locations.find((l) => l.id === id)?.name ?? '';

  const load = useCallback(async () => {
    try { setData(await get<AccessResp>('/api/admin/access')); setErr(''); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load this page.'); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const act = async (fn: () => Promise<void>) => {
    try { await fn(); await load(); } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };

  const people = (data?.people ?? []).filter((p) => store === 'all' || p.homeLocationId === store || p.extraLocationIds.includes(store));
  const toggleAccess = (userId: number | null, name: string, off: Off | null) => {
    if (!userId) { showToast(`${name} hasn’t signed in yet, so there’s nothing to turn off.`); return; }
    if (off) { void act(async () => { await post(`/api/admin/people/${userId}/access`, { off: false }); }); return; }
    setConfirm({
      title: `Turn off access for ${name}?`,
      text: `${name} is signed out of the desktop, office computer, counter screen and their phone’s Store tab at once, and can’t sign in again until you turn it back on. They stay on the schedule.`,
      ok: 'Turn off', run: () => act(async () => { await post(`/api/admin/people/${userId}/access`, { off: true }); }),
    });
  };
  const resetPin = (userId: number | null, name: string) => {
    if (!userId) return;
    setConfirm({ title: `Reset ${name}’s PIN?`, text: 'Their PIN is removed and they’re signed out everywhere. Next time they sign in with a new code and pick a new PIN.',
      ok: 'Reset PIN', run: () => act(async () => { await post(`/api/admin/people/${userId}/reset-pin`); }) });
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '10px 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 520px' }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Managers &amp; access</h1>
          <div style={{ fontSize: 14, color: '#4A5561' }}>Who can sign in to manage stores, and the office computers and counter screens. Only admins see this page. Everyone signs in with their own mobile number and PIN.</div>
        </div>
        <label className="acclbl">Show
          <select className="sel-sm" value={store} onChange={(e) => setStore(e.target.value === 'all' ? 'all' : Number(e.target.value))} aria-label="Which store">
            <option value="all">All stores</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <button className="tbtn primary" onClick={() => setAdding(true)}>Add someone…</button>
      </div>
      {err && <div className="errbar">{err}</div>}
      {data && (
        <>
          <div className="accsum">
            <span className={'accpill' + (data.summary.admins >= data.summary.maxAdmins ? ' full' : '')}>Admins {data.summary.admins} of {data.summary.maxAdmins}</span>
            <span className="accpill">Managers {data.summary.managers}</span>
            <span className="accpill">Shift leads {data.summary.leads}</span>
            <span className="accpill">HR {data.summary.hr}</span>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid #C9CEC6', borderRadius: 8, background: '#FFFFFF' }}>
            <table className="tbl acctbl" style={{ minWidth: 1080 }}>
              <thead><tr><th>Name</th><th>Role</th><th>Stores they manage</th><th>Mobile</th><th>PIN</th><th>Last signed in</th><th>Access</th></tr></thead>
              <tbody>
                {store === 'all' && data.staff.map((s) => (
                  <tr key={'s' + s.userId} className={s.off ? 'accoff' : ''}>
                    <td><b>{s.name}</b><div className="accsub">Not on a schedule</div></td>
                    <td><span className="accrole">{s.role === 'admin' ? 'Admin' : 'HR (read-only)'}</span></td>
                    <td><span className="accsub">All stores</span></td>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>{s.phone}</td>
                    <td>{s.pinSet
                      ? <><span className="accok">Set</span> <button className="linkbtn" onClick={() => resetPin(s.userId, s.name)}>Reset</button></>
                      : <><span className="accwait">Not set</span> <button className="linkbtn" onClick={() => act(async () => {
                        const r = await post<{ code: string; minutes: number }>(`/api/admin/people/${s.userId}/sign-in-code`);
                        setCode({ title: `Sign-in code for ${s.name}`, code: r.code, text: `On their phone they open mywunderland.com, type their mobile number, then this code, and pick a 6-digit PIN. It works once, for ${r.minutes} minutes.` });
                      })}>Sign-in code</button></>}</td>
                    <td style={{ color: '#4A5561' }}>{s.lastSignIn ?? '—'}<div><button className="linkbtn" style={{ fontSize: 12.5 }} onClick={() => setActFor({ userId: s.userId })}>Activity</button></div></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className={s.off ? 'accwait' : 'accok'}>{s.off ? `Off since ${s.off.on}${s.off.by ? ` (${s.off.by})` : ''}` : 'On'}</span>{' '}
                      <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={() => toggleAccess(s.userId, s.name, s.off)}>{s.off ? 'Turn back on' : 'Turn off'}</button>
                    </td>
                  </tr>
                ))}
                {people.map((p) => (
                  <tr key={'e' + p.employeeId} className={p.off ? 'accoff' : ''}>
                    <td><b>{p.name}</b><div className="accsub">Home store: {locName(p.homeLocationId)}</div></td>
                    <td>
                      <select className="sel-sm" value={p.level} disabled={!!p.off} aria-label={`Role for ${p.name}`}
                        onChange={(e) => act(async () => { await post('/api/admin/employee', { employeeId: p.employeeId, level: Number(e.target.value) }); })}>
                        <option value={9}>Store manager (9)</option><option value={8}>Assistant manager (8)</option>
                        <option value={7}>Day manager (7)</option><option value={6}>Shift lead (6)</option>
                      </select>
                    </td>
                    <td>
                      <div className="accchips">
                        {locations.filter((l) => l.kind === 'store' || l.id === p.homeLocationId).map((l) => {
                          const home = l.id === p.homeLocationId;
                          const on = home || p.extraLocationIds.includes(l.id);
                          const canChip = p.level >= 7 && !home && !p.off && !!p.userId;
                          return (
                            <button key={l.id} className={'accchip' + (on ? ' on' : '')} aria-pressed={on} disabled={!canChip}
                              title={home ? 'Home store' : p.level < 7 ? 'Shift leads cover their home store only' : !p.userId ? 'They need to sign in once first' : on ? `Stop managing ${l.name}` : `Also manage ${l.name}`}
                              onClick={() => act(async () => { await post(`/api/admin/people/${p.userId}/stores`, { locationId: l.id, on: !on }); })}>{l.name}</button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>{p.phone || '—'}</td>
                    <td>{p.pinSet
                      ? <><span className="accok">Set</span> <button className="linkbtn" onClick={() => resetPin(p.userId, p.name)}>Reset</button></>
                      : <><span className="accwait">Not set</span> <button className="linkbtn" onClick={() => act(async () => {
                        const r = await post<{ code: string; minutes: number; name: string }>(`/api/employees/${p.employeeId}/sign-in-code`);
                        setCode({ title: `Sign-in code for ${p.name}`, code: r.code, text: `On their phone they open mywunderland.com, type their mobile number, then this code, and pick their own PIN. It works once, for ${r.minutes} minutes.` });
                      })}>Sign-in code</button></>}</td>
                    <td style={{ color: '#4A5561' }}>{p.lastSignIn ?? '—'}<div><button className="linkbtn" style={{ fontSize: 12.5 }} onClick={() => setActFor({ employeeId: p.employeeId })}>Activity</button></div></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className={p.off ? 'accwait' : 'accok'}>{p.off ? `Off since ${p.off.on}${p.off.by ? ` (${p.off.by})` : ''}` : 'On'}</span>{' '}
                      <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={() => toggleAccess(p.userId, p.name, p.off)}>{p.off ? 'Turn back on' : 'Turn off'}</button>
                    </td>
                  </tr>
                ))}
                {people.length === 0 && (store !== 'all' || data.staff.length === 0) && (
                  <tr><td colSpan={7} style={{ color: '#4A5561' }}>No managers or shift leads yet. Add them on the Employees page (level 6–9), or with “Add someone…”.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 13, color: '#4A5561' }}>Managers (levels 7–9) get the desktop and office computer for their stores, plus the Store tab on their phone. Shift leads (6) unlock the counter screen and see who’s working on their phone. A person’s level here is the same as on the Employees page. Turning access off signs them out everywhere at once; they stay on the schedule (archive them on the Employees page if they’ve left).</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Locations</h2>
            <span style={{ flex: '1 1 auto' }} />
            <button className="btn2" onClick={() => setAddLoc(true)}>Add location…</button>
          </div>
          <div className="accsum">
            {locations.filter((l) => !l.demo).map((l) => <span key={l.id} className="accpill">{l.name}{l.kind === 'office' ? ' · office' : ''}</span>)}
          </div>
          <div style={{ fontSize: 13, color: '#4A5561' }}>Stores have stations, a day board, a rotation and a counter screen. Offices (like Operations) don’t: their people have a position instead, only admins manage them, and they can still fill in at a store.</div>

          <StoreEmails locations={locations.filter((l) => l.kind === 'store')} showToast={showToast} onChanged={onLocationsChanged} />

          <DemoBox stores={locations.filter((l) => l.kind === 'store' && !l.demo)} setConfirm={setConfirm} showToast={showToast} onChanged={onLocationsChanged} />

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Office computers and counter screens</h2>
            <span style={{ flex: '1 1 auto' }} />
            <button className="btn2" onClick={() => setDeviceModal(true)}>Set up a device…</button>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid #C9CEC6', borderRadius: 8, background: '#FFFFFF' }}>
            <table className="tbl" style={{ minWidth: 760 }}>
              <thead><tr><th>Device</th><th>Store</th><th>Set up</th><th>Last used</th><th>Status</th></tr></thead>
              <tbody>
                {data.devices.filter((d) => store === 'all' || d.locationId === store).map((d) => (
                  <tr key={d.id}>
                    <td><b>{locName(d.locationId)} {d.kind === 'office' ? 'office computer' : 'counter screen'}</b>
                      <div className="accsub">{d.kind === 'office' ? 'Lock screen of the store’s managers' : 'Shows the rotation; managers and shift leads unlock it'}</div>
                      {d.kind === 'office' && locations.find((l) => l.id === d.locationId)?.kind !== 'office' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6, fontSize: 13 }}>
                          <span style={{ color: '#4A5561' }}>Can open: Schedule</span>
                          {MODULES.map((m) => (
                            <label key={m.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!d.modules?.includes(m.key)} onChange={(e) => act(async () => {
                                const next = e.target.checked ? [...(d.modules ?? []), m.key] : (d.modules ?? []).filter((x) => x !== m.key);
                                await post(`/api/admin/devices/${d.id}/modules`, { modules: next });
                              })} />{m.name}
                            </label>
                          ))}
                        </div>
                      )}</td>
                    <td>{locName(d.locationId)}</td>
                    <td style={{ color: '#4A5561' }}>{d.setUpAt}{d.setUpBy ? ` by ${d.setUpBy}` : ''}</td>
                    <td style={{ color: '#4A5561' }}>{d.lastUsed ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}><span className="accok">On</span>{' '}
                      <button className="btn2" style={{ minHeight: 30, padding: '2px 10px' }} onClick={() => setConfirm({
                        title: 'Switch this device off?', text: 'It’s signed out at once and can’t be used until it’s set up again with a new code.',
                        ok: 'Switch off', run: () => act(async () => { await post(`/api/admin/devices/${d.id}/off`); }),
                      })}>Switch off</button></td>
                  </tr>
                ))}
                {data.devices.length === 0 && <tr><td colSpan={5} style={{ color: '#4A5561' }}>No devices set up yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 13, color: '#4A5561' }}>If a device is lost or replaced, switch it off here: it’s signed out at once and can’t be used until it’s set up again.</div>

          <ActivityList locations={locations} />

          <ChangeHistory locations={locations} />
        </>
      )}

      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {actFor && <ActivityModal employeeId={actFor.employeeId} userId={actFor.userId} onClose={() => setActFor(null)} />}
      {code && (
        <Modal title={code.title} onClose={() => setCode(null)}>
          <div className="codebig">{code.code}</div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>{code.text}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="tbtn primary" onClick={() => setCode(null)}>Done</button></div>
        </Modal>
      )}
      {addLoc && <AddLocation onClose={() => setAddLoc(false)} onDone={(msg) => { setAddLoc(false); onLocationsChanged(); showToast(msg); }} />}
      {adding && <AddSomeone locations={locations} onClose={() => setAdding(false)} onDone={async (msg) => { setAdding(false); await load(); showToast(msg); }} />}
      {deviceModal && <DeviceSetup locations={locations} onClose={() => setDeviceModal(false)}
        onCode={(c) => { setDeviceModal(false); setCode(c); }} />}
      {toast}
    </section>
  );
}

function AddSomeone({ locations, onClose, onDone }: { locations: Location[]; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  const [kind, setKind] = useState<'employee' | 'staff'>('employee');
  const [loc, setLoc] = useState<number>(locations.find((l) => l.kind === 'store')?.id ?? 0);
  const [emps, setEmps] = useState<{ id: number; name: string; level: number | null }[]>([]);
  const [empId, setEmpId] = useState<number | ''>('');
  const [level, setLevel] = useState(7);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'admin' | 'hr'>('admin');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (kind !== 'employee' || !loc) return;
    get<{ employees: { id: number; name: string; level: number | null }[] }>(`/api/employees?location=${loc}`)
      .then((r) => setEmps(r.employees.filter((e) => (e.level ?? 0) < 6))).catch(() => setEmps([]));
  }, [kind, loc]);

  const go = async () => {
    setErr('');
    try {
      if (kind === 'employee') {
        await post('/api/admin/employee', { employeeId: empId, level });
        await onDone(`${emps.find((e) => e.id === empId)?.name ?? 'They'} now ${level >= 7 ? 'manage' : 'lead shifts at'} ${locations.find((l) => l.id === loc)?.name}.`);
      } else {
        await post('/api/admin/staff', { name, phone, role });
        await onDone(`${name} added. Give them a sign-in code from the list.`);
      }
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };

  return (
    <Modal title="Add someone" onClose={onClose}>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className={'pill' + (kind === 'employee' ? ' on' : '')} onClick={() => setKind('employee')}>An employee</button>
        <button className={'pill' + (kind === 'staff' ? ' on' : '')} onClick={() => setKind('staff')}>Owner, admin or HR</button>
      </div>
      {kind === 'employee' ? (
        <>
          <div style={{ fontSize: 13, color: '#4A5561' }}>Gives someone already on the Employees page manager or shift-lead access.</div>
          <div className="addnote"><b>Adding attendants (levels 0–5) or Operations staff?</b> They’re added on the <a href="/employees">Employees page</a>, not here: pick the store, or <b>Operations</b> for techs and office staff, in Location at the top, then type them in. Their level or position is set there too.</div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Store
            <select value={loc} onChange={(e) => { setLoc(Number(e.target.value)); setEmpId(''); }}>{locations.filter((l) => l.kind === 'store').map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Employee
            <select value={empId} onChange={(e) => setEmpId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Pick someone…</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Role
            <select value={level} onChange={(e) => setLevel(Number(e.target.value))}>
              <option value={9}>Store manager (9)</option><option value={8}>Assistant manager (8)</option><option value={7}>Day manager (7)</option><option value={6}>Shift lead (6)</option>
            </select>
          </label>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#4A5561' }}>Someone who isn’t on a schedule. Admins can do everything (at most 5); HR can see everything but not change it.</div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Name<input className="inp" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Mobile number<input className="inp" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="503-555-0100" /></label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Role
            <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'hr')}><option value="admin">Admin</option><option value="hr">HR (read-only)</option></select>
          </label>
        </>
      )}
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" onClick={go} disabled={kind === 'employee' ? !empId : !name.trim() || phone.replace(/\D/g, '').length < 10}>Give access</button>
      </div>
    </Modal>
  );
}

function DeviceSetup({ locations, onClose, onCode }: {
  locations: Location[]; onClose: () => void; onCode: (c: { title: string; code: string; text: string }) => void;
}) {
  const [loc, setLoc] = useState<number>(locations[0]?.id ?? 0);
  const [kind, setKind] = useState<'office' | 'counter'>('counter');
  const [err, setErr] = useState('');
  const name = `${locations.find((l) => l.id === loc)?.name ?? ''} ${kind === 'office' ? 'office computer' : 'counter screen'}`;
  return (
    <Modal title="Set up a device" onClose={onClose}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Store
        <select value={loc} onChange={(e) => { const id = Number(e.target.value); setLoc(id); if (locations.find((l) => l.id === id)?.kind === 'office') setKind('office'); }}>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>What it is
        <select value={kind} onChange={(e) => setKind(e.target.value as 'office' | 'counter')}>
          <option value="office">Office computer</option>
          {locations.find((l) => l.id === loc)?.kind !== 'office' && <option value="counter">Counter screen</option>}
        </select>
      </label>
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" onClick={async () => {
          try {
            const r = await post<{ code: string; minutes: number }>('/api/admin/devices/setup-code', { locationId: loc, kind });
            onCode({ title: `Setup code for the ${name}`, code: r.code,
              text: `On the ${name}, open mywunderland.com/device and type this code. It works once, for ${r.minutes} minutes. After that the device remembers it’s the ${name}.` });
          } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t work.'); }
        }}>Make a setup code</button>
      </div>
    </Modal>
  );
}

function AddLocation({ onClose, onDone }: { onClose: () => void; onDone: (msg: string) => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'store' | 'office'>('office');
  const [err, setErr] = useState('');
  return (
    <Modal title="Add location" onClose={onClose}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Name
        <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Operations" autoFocus />
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
        <label className="allday"><input type="radio" checked={kind === 'office'} onChange={() => setKind('office')} />Office: techs and office staff, positions instead of stations, admins only</label>
        <label className="allday"><input type="radio" checked={kind === 'store'} onChange={() => setKind('store')} />Store: gets the standard stations, day board, rotation and counter screen</label>
      </div>
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" disabled={name.trim().length < 2} onClick={async () => {
          try { await post('/api/admin/locations', { name: name.trim(), kind }); onDone(`${name.trim()} added. Pick it in Location to add its people.`); }
          catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save.'); }
        }}>Add location</button>
      </div>
    </Modal>
  );
}
